import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { FaBalanceScale } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import QuoteStatusPill from "../../../components/QuoteStatusPill";
import { QuoteComparison, QuoteListItem } from "../../../models/Quote";
import { getQuoteComparison, getQuotes, linkQuoteProcess } from "../../../services/quoteService";
import { crm } from "../../../theme";
import SectionCard from "./SectionCard";
import { TableCard, tdProps, thProps } from "./tableStyles";

const brl = (value?: number | null) =>
  value == null ? "—" : value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const pct = (value?: number | null) =>
  value == null ? "—" : `${value > 0 ? "+" : ""}${value.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

/** Modal de busca de orçamento (número ou cliente) para vincular ao processo. */
const LinkQuoteModal = ({
  isOpen,
  onClose,
  onPick,
}: {
  isOpen: boolean;
  onClose: () => void;
  onPick: (quote: QuoteListItem) => Promise<string | null>;
}) => {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<QuoteListItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setBusy(true);
    const timer = setTimeout(async () => {
      const list = await getQuotes({ search: term.trim() || undefined });
      // Só orçamentos ainda sem processo podem ser vinculados
      setResults(list.filter((q) => !q.processId));
      setBusy(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [term, isOpen]);

  const pick = async (quote: QuoteListItem) => {
    setBusy(true);
    const problem = await onPick(quote);
    setBusy(false);
    if (problem) setError(problem);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius={crm.radius}>
        <ModalHeader fontSize="18px">Vincular orçamento</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Input size="sm" placeholder="Buscar por número, cliente ou produto" value={term} onChange={(e) => setTerm(e.target.value)} />
          {busy && (
            <Flex justify="center" py="12px">
              <Spinner size="sm" color={crm.brand} />
            </Flex>
          )}
          {!busy && results.length === 0 && (
            <Text mt="12px" fontSize="13px" color={crm.textMuted}>
              Nenhum orçamento disponível (sem processo vinculado).
            </Text>
          )}
          <Box mt="12px">
            {results.map((q) => (
              <Flex
                key={q.id}
                align="center"
                justify="space-between"
                gap="8px"
                p="8px 10px"
                border={`1px solid ${crm.border}`}
                borderRadius={crm.radiusSm}
                mb="6px"
              >
                <Box minW={0}>
                  <Flex align="center" gap="8px">
                    <Text fontSize="13px" fontWeight="600">
                      {q.number}
                    </Text>
                    <QuoteStatusPill status={q.status} />
                  </Flex>
                  <Text fontSize="12px" color={crm.textMuted} noOfLines={1}>
                    {q.clientName}
                    {q.product ? ` · ${q.product}` : ""} · v{q.currentVersion} · {brl(q.finalCostBrl)}
                  </Text>
                </Box>
                <Button size="xs" variant="outline" isDisabled={busy} onClick={() => pick(q)}>
                  Vincular
                </Button>
              </Flex>
            ))}
          </Box>
          {error && (
            <Text mt="8px" fontSize="13px" color={crm.danger}>
              {error}
            </Text>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

/**
 * Seção "Estimado × Realizado" da tela de processo: orçamento aprovado vinculado (estimado) contra a DUIMP e
 * os numerários (realizado). Só equipe interna; cliente nunca vê (nem chama a API).
 */
const EstimatedRealizedPanel = ({ processId }: { processId: string }) => {
  const auth = useAuthUser<any>();
  const isClient = auth?.position === 0 || auth?.position === 1;
  const navigate = useNavigate();
  const toast = useToast();
  const linkModal = useDisclosure();
  const unlinkDialog = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [data, setData] = useState<QuoteComparison | null | undefined>(undefined);

  const load = useCallback(async () => {
    const response = await getQuoteComparison(processId);
    setData(response ?? null);
  }, [processId]);

  useEffect(() => {
    if (!isClient) load();
  }, [isClient, load]);

  if (isClient || data === null) return null;

  const link = async (quote: QuoteListItem): Promise<string | null> => {
    const response = await linkQuoteProcess(quote.id!, processId);
    if (!response.success) return response.notifications[0] ?? "Não foi possível vincular.";
    linkModal.onClose();
    toast({ status: "success", title: `Orçamento ${quote.number} vinculado.` });
    load();
    return null;
  };

  const unlink = async () => {
    unlinkDialog.onClose();
    if (!data?.quoteId) return;
    const response = await linkQuoteProcess(data.quoteId, null);
    if (!response.success) {
      toast({ status: "error", title: response.notifications[0] ?? "Não foi possível desvincular." });
      return;
    }
    load();
  };

  return (
    <SectionCard
      icon={<FaBalanceScale />}
      title="Estimado × Realizado"
      subtitle={
        data?.linked
          ? `Orçamento ${data.number} · versão ${data.versionUsed ?? "—"} × DUIMP e numerários`
          : "Compare o orçamento aprovado com o que foi pago no processo"
      }
      actions={
        data?.linked ? (
          <>
            <Button size="sm" variant="outline" onClick={() => navigate(`/simulador?quote=${data.quoteId}`)}>
              Abrir orçamento
            </Button>
            <Button size="sm" variant="ghost" onClick={unlinkDialog.onOpen}>
              Desvincular
            </Button>
          </>
        ) : undefined
      }
    >
      {data === undefined ? (
        <Flex justify="center" py="16px">
          <Spinner color={crm.brand} />
        </Flex>
      ) : !data.linked ? (
        <Flex direction="column" align="center" gap="8px" py="12px">
          <Text fontSize="14px" color={crm.textMuted} textAlign="center">
            Nenhum orçamento vinculado a este processo.
          </Text>
          <Button size="sm" bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} onClick={linkModal.onOpen}>
            Vincular orçamento
          </Button>
        </Flex>
      ) : (
        <>
          <Flex align="center" gap="8px" mb="12px" wrap="wrap">
            <QuoteStatusPill status={data.status ?? "Rascunho"} />
            {!data.isApproved && (
              <Text fontSize="12px" color={crm.warning}>
                Orçamento ainda não aprovado: comparando com a versão atual.
              </Text>
            )}
            {!data.duimpAvailable && (
              <Text fontSize="12px" color={crm.textMuted}>
                Sem dados da DUIMP ainda: só o estimado está disponível.
              </Text>
            )}
          </Flex>
          <TableCard>
            <Thead>
              <Tr>
                <Th {...thProps}>Linha</Th>
                <Th {...thProps} isNumeric>
                  Estimado
                </Th>
                <Th {...thProps} isNumeric>
                  Realizado
                </Th>
                <Th {...thProps} isNumeric>
                  Diferença (R$)
                </Th>
                <Th {...thProps} isNumeric>
                  Diferença (%)
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.lines.map((line) => {
                const over = (line.diff ?? 0) > 0;
                const diffColor = line.diff == null || line.diff === 0 ? undefined : over ? crm.danger : crm.success;
                return (
                  <Tr key={line.key} bg={line.isTotal ? crm.subtle : undefined}>
                    <Td {...tdProps} fontWeight={line.isTotal ? 700 : 500}>
                      {line.label}
                    </Td>
                    <Td {...tdProps} isNumeric whiteSpace="nowrap" fontWeight={line.isTotal ? 700 : 400}>
                      {brl(line.estimated)}
                    </Td>
                    <Td {...tdProps} isNumeric whiteSpace="nowrap" fontWeight={line.isTotal ? 700 : 400}>
                      {brl(line.realized)}
                    </Td>
                    <Td {...tdProps} isNumeric whiteSpace="nowrap" color={diffColor} fontWeight={600}>
                      {line.diff == null ? "—" : brl(line.diff)}
                    </Td>
                    <Td {...tdProps} isNumeric whiteSpace="nowrap" color={diffColor} fontWeight={600}>
                      {pct(line.diffPercent)}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </TableCard>
          <Text fontSize="11px" color={crm.textFaint} mt="8px">
            Diferença = realizado − estimado (vermelho = acima do orçamento). Tributos pelo valor pago na DUIMP (ou devido, se ainda
            não pago); despesas = numerários lançados + Taxa Siscomex, sem os previstos. ICMS via trading só existe no estimado.
          </Text>
        </>
      )}

      <LinkQuoteModal isOpen={linkModal.isOpen} onClose={linkModal.onClose} onPick={link} />

      <AlertDialog isOpen={unlinkDialog.isOpen} leastDestructiveRef={cancelRef} onClose={unlinkDialog.onClose} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius={crm.radius}>
            <AlertDialogHeader fontSize="lg" fontWeight="700">
              Desvincular orçamento
            </AlertDialogHeader>
            <AlertDialogBody>Desvincular o orçamento {data?.number} deste processo? O orçamento continua salvo no CRM.</AlertDialogBody>
            <AlertDialogFooter gap="8px">
              <Button ref={cancelRef} onClick={unlinkDialog.onClose}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={unlink}>
                Desvincular
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </SectionCard>
  );
};

export default EstimatedRealizedPanel;
